import { useEffect, useRef, useState } from "react";
import privateAxios from "../utils/axiosPrivate";
import {
  getAllRolesUrl,
  navItemsURL,
  getUserMngRoleWithPermissionsURL
} from "../utils/constants/apiEndPoints";
import { showToast } from "../utils/utils";
import {
  ALPHABETIC_REGEX,
  showToastType
} from "../utils/constants/appConstants";
import { roleMange } from "../locales/en";
import {
  getNextOrderNumber,
  buildRolePayload
} from "../utils/constants/roleConfig";
import { isRadioGroupNavItem } from "../utils/roleUtils";

export const useCreateRole = ({ onSuccess } = {}) => {
  const [roleName, setRoleName] = useState("");
  const [permissionType, setPermissionType] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [adminMenuPermissions, setAdminMenuPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderNumber, setOrderNumber] = useState("1");
  const [errOrderMessage, setErrOrderMessage] = useState("");
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false);
  const [roleNameError, setRoleNameError] = useState("");
  const [checkedPermissions, setCheckedPermissions] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});

  const roleNameRef = useRef(null);
  const permissionAccessRef = useRef(null);
  const orderNumberRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchInitial = async () => {
      try {
        setLoading(true);
        const [rolesRes, navRes] = await Promise.all([
          privateAxios.get(getAllRolesUrl),
          privateAxios.get(navItemsURL)
        ]);

        if (!isMounted) return;

        const roles = rolesRes?.data?.data || [];
        const navItems = navRes?.data?.data || [];

        setOrderNumber(getNextOrderNumber(roles));
        setAdminMenuPermissions(navItems);
      } catch (error) {
        const msg = error?.response?.data?.message || error.message;
        showToast(msg, showToastType.error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchInitial();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectAll = (itemId, checked) => {
    setCheckedItems((prev) => {
      const updated = { ...prev, [itemId]: checked };

      const item = adminMenuPermissions.find((i) => i.id === itemId);
      if (item?.children?.length) {
        item.children.forEach((child) => {
          updated[child.id] = checked;
        });
      }

      return updated;
    });
  };

  const handleChildChange = (itemId, childId, checked) => {
    setCheckedItems((prev) => {
      const updated = { ...prev };
      const parent = adminMenuPermissions.find((i) => i.id === itemId);

      if (parent && isRadioGroupNavItem(parent.slugName)) {
        parent.children.forEach((child) => {
          updated[child.id] = false;
        });
        updated[childId] = checked;
        updated[itemId] = checked;
      } else {
        updated[childId] = checked;
        const allChildrenChecked = parent?.children?.every(
          (child) => updated[child.id]
        );
        updated[itemId] = !!allChildrenChecked;
      }

      return updated;
    });
  };

  const handlePermissionTypeChange = (event) => {
    const { value } = event.target;
    const isSelected = value !== "false";
    setPermissionType(isSelected);
  };

  const handleShortChange = (e) => {
    const value = e.target.value;

    if (value.startsWith("0")) return;

    if (value && value.length > 0) {
      setErrOrderMessage("");
    }

    if (value.length <= 6) {
      setOrderNumber(value);
    }
  };

  const handleRoleNameChange = (event) => {
    const value = event.target.value;

    if (value === "") {
      setRoleName("");
      setRoleNameError("");
      return;
    }

    const isValidName = ALPHABETIC_REGEX.test(value);

    if (isValidName) {
      setRoleName(value);
      setRoleNameError("");
    } else {
      setRoleNameError(roleMange.errorMsg.rolenameErrorSet);
    }
  };

  const handleTfaChange = (event) => {
    const { value } = event.target;
    const isSelected = value !== "false";
    setIsTwoFactorEnabled(isSelected);
  };

  const resetForm = () => {
    setRoleName("");
    setPermissionType(true);
    setPermissions({});
    setCheckedPermissions([]);
    setCheckedItems({});
    setOrderNumber("1");
    setIsTwoFactorEnabled(false);
    setRoleNameError("");
    setErrOrderMessage("");
  };

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    setRoleNameError("");
    setErrOrderMessage("");

    let isValid = true;

    if (!roleName.trim()) {
      setRoleNameError(roleMange.errorMsg.roleNameError);
      roleNameRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      isValid = false;
    }

    if (!orderNumber) {
      setErrOrderMessage(roleMange.errorMsg.orderNumberError);
      orderNumberRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      isValid = false;
    }

    if (!isValid) return;

    try {
      const payload = buildRolePayload({
        roleName,
        permissionType,
        checkedPermissions,
        orderNumber,
        isTwoFactorEnabled,
        checkedItems
      });

      const response = await privateAxios.post(
        getUserMngRoleWithPermissionsURL,
        payload
      );
      showToast(response.data.message, showToastType.success);
      resetForm();

      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;

        if (status === 400 && Array.isArray(data.errors)) {
          const errorMessages = data.errors
            .map((errorObj) => Object.values(errorObj).flat().join(", "))
            .join(", ");
          showToast(errorMessages, showToastType.error);
        } else if (status === 409) {
          showToast(error.response.data.errors, showToastType.error);
        } else {
          showToast(error.message, showToastType.error);
        }
      } else {
        showToast(error.message, showToastType.error);
      }
    }
  };

  return {
    loading,
    roleName,
    permissionType,
    permissions,
    adminMenuPermissions,
    orderNumber,
    errOrderMessage,
    isTwoFactorEnabled,
    roleNameError,
    checkedPermissions,
    checkedItems,
    roleNameRef,
    permissionAccessRef,
    orderNumberRef,

    handleRoleNameChange,
    handleTfaChange,
    handleShortChange,
    handlePermissionTypeChange,
    handleSelectAll,
    handleChildChange,
    handleSubmit,
    resetForm
  };
};
